import React from "react"
import { useTranslation } from "react-i18next"
import { Platform } from "react-native"

import styled from "styled-components/native"

import { getOptimizedImageSource } from "@treefort/lib/get-optimized-image-source"
import icons from "@treefort/tokens/app/icons"

import { useTrack } from "../hooks/audio"
import useHover from "../hooks/use-hover"
import { Track } from "../lib/audio-player"
import { kebabCase } from "../lib/kebab-case"
import Controls from "./audio-player-controls"
import Progress from "./audio-player-progress"
import AudioPlayerTitle from "./audio-player-title"
import Column from "./column"
import Icon from "./icon"
import Image from "./image"
import Row from "./row"
import { useTokens } from "./tokens-provider"
import Touchable from "./touchable"

const Container = styled(Row)`
  position: ${Platform.OS === "web" ? "fixed" : "absolute"};
  bottom: ${(props) => props.theme.spacing.pagePaddingHorizontal}px;
  right: ${(props) => props.theme.spacing.pagePaddingHorizontal}px;
  padding: ${(props) =>
    props.theme.audioPlayerFloating.padding -
    props.theme.audioPlayerFloating.borderWidth}px;
  border-radius: ${(props) => props.theme.audioPlayerFloating.borderRadius}px;
  border-width: ${(props) => props.theme.audioPlayerFloating.borderWidth}px;
  border-color: ${(props) => props.theme.audioPlayerFloating.borderColor};
  box-shadow: 0 0
    ${(props) => props.theme.audioPlayerFloating.dropShadow.radius}px
    ${(props) => props.theme.audioPlayerFloating.dropShadow.color};
`

const Artwork = styled(Image)`
  width: ${(props) => props.theme.audioPlayerFloating.artworkSize}px;
  height: ${(props) => props.theme.audioPlayerFloating.artworkSize}px;
  border-radius: ${(props) => props.theme.borderRadius.rounded}px;
  background-color: ${(props) => props.theme.colors.loading.image};
`

const TitleColumn = styled(Column)`
  min-width: 200px;
  max-width: 340px;
`

const ControlsContainer = styled.View`
  margin-top: -${(props) => props.theme.audioPlayerFloating.padding}px;
  align-items: center;
`

const CloseButtonContainer = styled.View<{ $visible: boolean }>`
  position: absolute;
  top: -${(props) => props.theme.audioPlayerFloating.closeButton.size / 2}px;
  right: -${(props) => props.theme.audioPlayerFloating.closeButton.size / 2}px;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
`

const CloseButton = styled(Touchable)`
  width: ${(props) => props.theme.audioPlayerFloating.closeButton.size}px;
  height: ${(props) => props.theme.audioPlayerFloating.closeButton.size}px;
  background-color: ${(props) =>
    props.theme.audioPlayerFloating.closeButton.backgroundColor};
  align-items: center;
  justify-content: center;
  border-radius: ${(props) =>
    props.theme.audioPlayerFloating.closeButton.borderRadius}px;
`

export default function AudioPlayerFloating({
  onPressTitle,
  onRequestClose,
}: {
  onPressTitle?: (track: Track) => void
  onRequestClose?: () => void
}): React.JSX.Element {
  const track = useTrack()
  const { tokens } = useTokens()
  const [isHovering, hoverProps] = useHover()
  const { t } = useTranslation()
  const navigateToContent = () => track && onPressTitle?.(track)

  return (
    <Container
      gap="medium"
      alignItems="center"
      backgroundColor="primary"
      {...hoverProps}
    >
      <Touchable
        id="audio-player-floating-artwork-touchable"
        onPress={navigateToContent}
      >
        <Artwork
          source={getOptimizedImageSource(
            { uri: track?.artwork },
            tokens.audioPlayerFloating.artworkSize,
          )}
          resizeMode="cover"
        />
      </Touchable>
      <TitleColumn>
        {track ? (
          <AudioPlayerTitle onPress={onPressTitle} track={track} />
        ) : null}
        <Progress compact />
      </TitleColumn>
      <ControlsContainer>
        <Controls compact onPressNavigateToContent={navigateToContent} />
      </ControlsContainer>
      {onRequestClose ? (
        <CloseButtonContainer $visible={isHovering}>
          <CloseButton
            id={kebabCase(
              `audio-player-floating-close-${track?.album || "unknown-album"}-${track?.title || "unknown-track"}`,
            )}
            role="button"
            aria-label={t("Close")}
            onPress={onRequestClose}
          >
            <Icon
              source={icons.closeCircle.fill}
              size="medium"
              color="secondary"
            />
          </CloseButton>
        </CloseButtonContainer>
      ) : null}
    </Container>
  )
}
